import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { api, isPermanenteFout } from "@/lib/api";
import { registerFlush } from "@/lib/flushRegistry";
import { zetJaNee, isJaNee } from "./jaNee";

const STATUS_TEKST = { bezig: "Opslaan…", ok: "Opgeslagen ✓", fout: "Niet opgeslagen" };

// v19-B: één autosave-veld van het weekoverleg. Stuurt alleen dit veld (per-field PUT).
// v19-R1 R7: één save tegelijk per veld (daarna de laatste tekst), status per veld, keepalive telt alleen bij 2xx.
export function AutoVeld({ veldId, pad, body, waarde, onStatus, testid, rijen = 2, placeholder, snel }) {
  const [tekst, setTekst] = useState(waarde || "");
  const [status, setStatus] = useState(null);
  const tekstRef = useRef(tekst);
  const opgeslagen = useRef(waarde || "");
  const bezig = useRef(false);
  const levend = useRef(true);
  const timer = useRef(null);
  const keepaliveTekst = useRef(null);
  const reeks = useRef(0);
  const permanent = useRef(false);   // v24-R1 F4: de server weigert → niet herhalen tot de volgende wijziging
  tekstRef.current = tekst;

  const meld = (s) => { if (levend.current) { setStatus(s); onStatus(veldId, s); } };
  const bewaar = async () => {
    clearTimeout(timer.current);
    if (bezig.current) return;   // de lopende save stuurt daarna zelf de laatste tekst
    let gedaan = false;
    while (tekstRef.current !== opgeslagen.current) {
      const t = tekstRef.current;
      bezig.current = true;
      meld("bezig");
      try {
        await api.put(pad, body(t));
        opgeslagen.current = t;
        gedaan = true;
        reeks.current = 0;
      } catch (e) {
        bezig.current = false;
        meld("fout");
        reeks.current += 1;
        permanent.current = isPermanenteFout(e);
        // v24-R1 F1: na het sluiten van het paneel altijd melden; anders alleen de eerste fout op rij of een weigering
        if (!levend.current) toast.error("Een antwoord in het weekoverleg kon niet worden opgeslagen. Open het overleg opnieuw.");
        else if (reeks.current === 1 || permanent.current) toast.error(e?.response?.data?.detail || "Opslaan mislukt. Je tekst staat nog in het vak.");
        // v24 R5: zelf opnieuw proberen met oplopende wachttijd, tot het lukt (tekst blijft in het vak); niet bij een weigering
        if (levend.current && !permanent.current) timer.current = setTimeout(() => bewaarRef.current(), Math.min(1000 * 2 ** (reeks.current - 1), 15000));
        return;
      }
      bezig.current = false;
    }
    if (gedaan) meld("ok");
  };
  const bewaarRef = useRef(bewaar);
  bewaarRef.current = bewaar;
  useEffect(() => {
    levend.current = true;   // StrictMode: effect draait mount→cleanup→mount; zonder dit blijft de status altijd weg
    const tabDicht = () => {
      const t = tekstRef.current;
      if (t === opgeslagen.current || t === keepaliveTekst.current) return;
      keepaliveTekst.current = t;
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api${pad}`, {
        method: "PUT", keepalive: true,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("tw1_token")}` },
        body: JSON.stringify(body(t)),
      }).then((res) => {
        if (!res.ok) throw new Error(String(res.status));
        opgeslagen.current = t;
        reeks.current = 0;   // v24-R1 F1
        if (tekstRef.current === t) meld("ok");
      }).catch(() => {
        keepaliveTekst.current = null;
        meld("fout");
        toast.error("Een antwoord in het weekoverleg kon niet worden opgeslagen. Open het overleg opnieuw.");
      });
    };
    const verborgen = () => {
      if (document.visibilityState === "hidden") tabDicht();
      // v24-R1 F3: terug zichtbaar en nog niet opgeslagen → zelf opnieuw proberen
      else if (tekstRef.current !== opgeslagen.current && !bezig.current && !permanent.current) bewaarRef.current();
    };
    window.addEventListener("pagehide", tabDicht);
    document.addEventListener("visibilitychange", verborgen);
    const afmelden = registerFlush(() => bewaarRef.current());
    return () => {
      afmelden();
      window.removeEventListener("pagehide", tabDicht);
      document.removeEventListener("visibilitychange", verborgen);
      levend.current = false;   // statussen van een verdwenen veld komen nooit op het nieuwe paneel
      bewaarRef.current();      // gewone save loopt door; een fout geeft een toast
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const wijzig = (v, direct) => {
    setTekst(v); tekstRef.current = v; clearTimeout(timer.current); permanent.current = false;
    if (direct) bewaarRef.current(); else timer.current = setTimeout(() => bewaarRef.current(), 1200);
  };
  return (
    <div>
      {snel && (
        <div className="grid grid-cols-2 gap-1.5 mt-1.5 mb-1.5" data-testid={`${testid}-snel`}>
          {["Ja", "Nee"].map((k) => (
            <button key={k} type="button" data-testid={`${testid}-${k.toLowerCase()}`} aria-pressed={isJaNee(tekst, k)} onClick={() => wijzig(zetJaNee(tekst, k), true)}
              className={`h-8 rounded-md border text-sm font-semibold transition-colors ${isJaNee(tekst, k) ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"}`}>{k}</button>
          ))}
        </div>
      )}
      <textarea data-testid={testid} value={tekst} rows={rijen} maxLength={snel ? 2000 : 10000} placeholder={placeholder}
        onChange={(e) => wijzig(e.target.value)} onBlur={() => { if (!permanent.current) bewaar(); }}
        className="w-full px-2.5 py-2 rounded-md border border-zinc-300 text-[13px] text-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-600" />
      {status && <span data-testid={`${testid}-status`} className={`block text-[11px] font-semibold ${status === "fout" ? "text-rose-600" : "text-zinc-400"}`}>{STATUS_TEKST[status]}</span>}
    </div>
  );
}
